import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Phone, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { GAM_TICKET_LOGO } from '@/config/assets';
import { STADIUM_BG } from '@/config/assets';

export default function ForgotPasswordPage() {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(''); // For development only
  const [method, setMethod] = useState('email');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!identifier) {
      toast.error(`Please enter your ${method === 'email' ? 'email address' : 'phone number'}`);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/user/forgot-password`, {
        identifier
      });
      
      setSent(true);
      // For development - store the token to show the reset link
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      toast.success('Reset instructions sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${STADIUM_BG})` }}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6">
          <Link to="/">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/30 shadow-xl mx-auto">
              <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
            </div>
          </Link>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 pb-20">
          <Card className="w-full max-w-md bg-white/95 backdrop-blur-xl shadow-2xl border-0 rounded-2xl overflow-hidden">
            <CardHeader className="text-center pb-2 pt-6">
              <CardTitle className="text-2xl font-bold text-slate-800">
                {sent ? 'Check Your Inbox' : 'Forgot Password?'}
              </CardTitle>
              <p className="text-slate-500 text-sm mt-2">
                {sent 
                  ? `We've sent reset instructions to your ${method === 'email' ? 'email' : 'phone'}`
                  : "No worries! Enter your email or phone and we'll send you reset instructions"
                }
              </p>
            </CardHeader>

            <CardContent className="pb-6">
              {sent ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  
                  {/* Development only - show reset link */}
                  {resetToken && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <p className="text-xs text-yellow-800 font-medium mb-2">
                        Development Mode - Reset Link:
                      </p>
                      <Link 
                        to={`/reset-password?token=${resetToken}`}
                        className="text-sm text-[#0055FF] hover:underline break-all"
                      >
                        Click here to reset your password
                      </Link>
                    </div>
                  )}
                  
                  <div className="mt-6 space-y-3">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => { setSent(false); setIdentifier(''); setResetToken(''); }}
                    >
                      Try different email/phone
                    </Button>
                    <Link to="/" className="block">
                      <Button variant="ghost" className="w-full text-slate-600">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Login
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  {/* Method Toggle */}
                  <div className="flex gap-2 p-1 bg-slate-100 rounded-xl mb-5">
                    <button
                      type="button"
                      onClick={() => { setMethod('email'); setIdentifier(''); }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        method === 'email' 
                          ? 'bg-white shadow-sm text-slate-800' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMethod('phone'); setIdentifier(''); }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        method === 'phone' 
                          ? 'bg-white shadow-sm text-slate-800' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      Phone
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block text-slate-700">
                        {method === 'email' ? 'Email Address' : 'Phone Number'}
                      </label>
                      <div className="relative">
                        {method === 'email' ? (
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        ) : (
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        )}
                        <Input
                          type={method === 'email' ? 'email' : 'tel'}
                          placeholder={method === 'email' ? 'your@email.com' : '+220 XXX XXXX'}
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          className="pl-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                          required
                          data-testid="forgot-identifier"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-11 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] transition-all duration-300 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                      disabled={loading}
                      data-testid="forgot-submit"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Reset Instructions
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <Link 
                      to="/" 
                      className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-[#0055FF] transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Login
                    </Link>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </main>

        {/* Footer */}
        <footer className="p-4 text-center">
          <p className="text-white/50 text-sm">
            © 2026 Gam Ticket. The Premier Ticketing Platform for The Gambia.
          </p>
        </footer>
      </div>
    </div>
  );
}
